import {
  CurrentBooking,
  ReassignSelectOption
} from "PFApp/booking/components/reassign_booking_modal/bookings_to_reassign_selector.types";
import { SelectedUserData } from "PFApp/booking/components/reassign_booking_modal/reassign_booking_modal";
import { useFutureBookings } from "PFApp/booking/hooks/use_future_bookings";
import { useRowsChildrenInvalidate } from "PFApp/booking/store";
import { useGrowlStoreActions } from "PFApp/stores/growl_store";
import { utc } from "PFCore/helpers/date";
import { useBookingInvalidate } from "PFCore/hooks/queries";
import { useActivityInvalidate } from "PFCore/hooks/queries/activity";
import { useCreateAuditBookingPlaceholder } from "PFCore/hooks/queries/bookings/audit_bookings_approvals/use_create_audit_booking_placeholder";
import { useProjectsInvalidate } from "PFCore/hooks/queries/bookings/projects/use_projects_invalidate";
import { useWorkforceInvalidate } from "PFCore/hooks/queries/bookings/workforce/use_workforce_invalidate";
import { AuditBoookingPlaceholderPayload } from "PFCore/services/audit_bookings_approval";
import { BasicProfile, Booking, Id } from "PFCore/types";
import { useTranslation } from "react-i18next";

import { ProfileFrom } from "./reassign_action_context_provider";
import { areRequestApprovalDataFilled } from "./use_request_approval_submit.utils";

type GetBookingsToReassignParams = {
  currentBookings: CurrentBooking[];
  bookings: Booking[];
  reassignOption: ReassignSelectOption;
};

export type RequestApprovalSubmitData = {
  activityId: Id | undefined;
  profileFrom: ProfileFrom | undefined;
  profileTo: SelectedUserData["profile"];
  finalApprover: BasicProfile | null;
  note: string;
  reassignOption: ReassignSelectOption;
  currentBookings: CurrentBooking[];
};

type UseRequestApprovalSubmit = {
  currentBookings: CurrentBooking[];
  activityId: number | undefined;
  profileId: number | undefined;
};

export const useRequestApprovalSubmit = ({
  currentBookings,
  activityId,
  profileId
}: UseRequestApprovalSubmit) => {
  const { t } = useTranslation("bookingModule");
  const { showGrowl } = useGrowlStoreActions();

  const { invalidateProfileBookingsList } = useBookingInvalidate();
  const { invalidate: invalidateWorkforce } = useWorkforceInvalidate();
  const { invalidate: invalidateProjects } = useProjectsInvalidate();
  const { invalidateByActivityIds } = useRowsChildrenInvalidate();
  const { invalidate: invalidateActivities } = useActivityInvalidate();

  const { bookings: allBookings } = useFutureBookings({
    thisBooking: { id: currentBookings[0]?.id, startDate: currentBookings[0]?.startDate as string },
    activityId,
    profileId: profileId as number
  });

  const getBookingsToReassign = ({
    currentBookings,
    bookings,
    reassignOption
  }: GetBookingsToReassignParams): Id[] => {
    switch (reassignOption) {
      case ReassignSelectOption.ThisBooking:
        return currentBookings.map(({ id }) => id);
      case ReassignSelectOption.ThisAndFuture:
        return bookings
          .filter(({ start_date }) => utc(start_date).isSameOrAfter(utc(currentBookings[0]?.startDate))) // assume this option is available only for single booking reassignment
          .map(({ id }) => id);
      case ReassignSelectOption.AllBookings:
        return bookings.map(({ id }) => id);
      default:
        return [];
    }
  };

  const { create: createAuditBookingPlaceholder } = useCreateAuditBookingPlaceholder({
    onSuccess: (_, { profileId, oldProfileId, activityId }) => {
      showGrowl({
        kind: "success",
        message: t("bookings.reassign.requestApprovalSuccess")
      });

      [profileId, oldProfileId].forEach((id) => invalidateProfileBookingsList(id as number));
      invalidateWorkforce();
      invalidateProjects();
      invalidateByActivityIds([activityId as number]);
      invalidateActivities([activityId as number]);
    },
    onError: () => {
      showGrowl({
        kind: "error",
        message: t("bookings.reassign.requestApprovalError")
      });
    }
  });

  const requestApprovalSubmit = (data: RequestApprovalSubmitData) => {
    if (!areRequestApprovalDataFilled(data)) {
      showGrowl({
        kind: "error",
        message: t("bookings.reassign.requestApprovalErrorContent")
      });
      return;
    }

    const { activityId, profileFrom, profileTo, finalApprover, note, reassignOption, currentBookings } = data;

    const bookingsIds = getBookingsToReassign({
      currentBookings,
      bookings: allBookings?.entries ?? [],
      reassignOption
    });

    const placeholderData: AuditBoookingPlaceholderPayload = {
      ids: bookingsIds,
      activityId: activityId!,
      profileId: profileTo!.id,
      oldProfileId: profileFrom!.id,
      approverId: finalApprover!.id,
      requestReason: note
    };

    createAuditBookingPlaceholder(placeholderData);
  };

  return {
    requestApprovalSubmit
  };
};
